import { Box, Chip, Paper, Skeleton, Typography } from "@mui/material";
import { useDrillDownDialog } from "../DrillDownProvider";
import { DrillDownConfig, WidgetExplanationContent } from "../types";
import { WidgetExplanation } from "./WidgetExplanation";
import { createColoredCardSx } from "./widgetStyles";

type StatCardProps = {
    label: string;
    value: number | string | undefined;
    color: string;
    explanation: WidgetExplanationContent;
    isLoading?: boolean;
    drillDown?: DrillDownConfig;
    // Chips opcionais por baixo do valor (ex.: repartição por clínica "Porto: 20") — texto já formatado.
    chips?: Array<string>;
    // Segunda LINHA de chips, para uma repartição por outra dimensão (ex.: clínica em cima, tipo de cliente
    // em baixo). Numa linha só, duas repartições diferentes leem-se como uma e confundem.
    secondaryChips?: Array<string>;
    sx?: object;
};

const chipRowSx = { height: 22, bgcolor: "rgba(255, 255, 255, 0.22)", color: "#fff", fontSize: "0.72rem", fontWeight: 600, "& .MuiChip-label": { px: 0.9 } };

const renderChipRow = (rowChips: Array<string>) => (
    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={0.5}>
        {rowChips.map((chip) => (
            <Chip key={chip} label={chip} size="small" sx={chipRowSx} />
        ))}
    </Box>
);

export const StatCard = ({ label, value, color, explanation, isLoading, drillDown, chips, secondaryChips, sx }: StatCardProps) => {
    const drillDownDialog = useDrillDownDialog();
    const isClickable = Boolean(drillDown && drillDownDialog && !isLoading);

    return (
        <Paper
            elevation={0}
            onClick={isClickable ? () => drillDownDialog!.openDrillDown(drillDown!) : undefined}
            sx={{
                ...createColoredCardSx(color),
                height: "100%",
                minHeight: 92,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                gap: 1,
                ...(isClickable ? { cursor: "pointer", "&:hover": { filter: "brightness(1.06)" } } : {}),
                ...sx,
            }}
        >
            <Box position="absolute" top={4} right={4}>
                <WidgetExplanation title={label} explanation={explanation} iconColor="rgba(255, 255, 255, 0.85)" />
            </Box>
            <Typography variant="body2" fontWeight={500} sx={{ color: "rgba(255, 255, 255, 0.92)" }}>
                {label}
            </Typography>
            {isLoading ? (
                <Skeleton variant="text" width={48} sx={{ fontSize: "2rem", bgcolor: "rgba(255, 255, 255, 0.3)" }} />
            ) : (
                <Typography variant="h4" fontWeight={700} color="#fff">
                    {value ?? "—"}
                </Typography>
            )}
            {!isLoading && chips && chips.length > 0 && renderChipRow(chips)}
            {!isLoading && secondaryChips && secondaryChips.length > 0 && renderChipRow(secondaryChips)}
        </Paper>
    );
};
